import type { MutableRefObject } from 'react';
import { TableTypes } from '@/types';
import type { Tables } from '../dbFunctions/useDBFunctions';
import { getCombosByStore, normalizeComboName } from './comboCartUtils';

type GetOnDB = <T extends Tables>(
    name: T,
    client?: number,
    retryCount?: number,
    force?: boolean,
    enableFillTables?: boolean
) => Promise<TableTypes[T]>;

type TablesRef = MutableRefObject<{ combo?: TableTypes['combo'] }>;

export const EMPTY_COMBOS: TableTypes['combo'] = [];

type NormalizedStoreCombo = ReturnType<typeof getCombosByStore>[number] & {
    nome_campanha: string;
};

let sharedCombos: TableTypes['combo'] = EMPTY_COMBOS;
let loadPromise: Promise<TableTypes['combo']> | null = null;
let cachedNormalizedCombos: NormalizedStoreCombo[] = [];
let cachedNormalizedCombosKey = '';
const listeners = new Set<() => void>();

const notifyListeners = () => {
    listeners.forEach((listener) => listener());
};

const setSharedCombos = (next: TableTypes['combo']) => {
    if (sharedCombos === next) return;
    sharedCombos = next;
    cachedNormalizedCombosKey = '';
    notifyListeners();
};

export const subscribeComboData = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getComboDataSnapshot = () => sharedCombos;

export const getNormalizedCombosForStore = (
    source: unknown,
    codLoja?: number
): NormalizedStoreCombo[] => {
    const storeCombos = getCombosByStore(source, codLoja);
    const key = `${codLoja ?? ''}:${storeCombos.map((combo) => combo.id_campanha).join('|')}`;

    if (key === cachedNormalizedCombosKey) {
        return cachedNormalizedCombos;
    }

    cachedNormalizedCombos = storeCombos.map((combo) => ({
        ...combo,
        nome_campanha: normalizeComboName(combo),
    }));
    cachedNormalizedCombosKey = key;

    return cachedNormalizedCombos;
};

export const ensureComboDataLoaded = async (
    getOnDB: GetOnDB,
    tables: TablesRef
): Promise<TableTypes['combo']> => {
    const cached = tables.current.combo;
    if (Array.isArray(cached)) {
        setSharedCombos(cached);
        return cached;
    }

    if (loadPromise) return loadPromise;

    loadPromise = getOnDB('combo')
        .then((data) => {
            const next = data ?? EMPTY_COMBOS;
            setSharedCombos(next);
            return next;
        })
        .finally(() => {
            loadPromise = null;
        });

    return loadPromise;
};
